import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { resourceScopeCleared, userScopeCleared } from '@krp/client-runtime'

import { dropResource } from '../../../shared/model/keys'
import { seatKey, type SeatRef } from './saleSlice'
import type { CartSendFailure, SaleCommand, SaleCommandKind, SaleCommandPhase, SaleCommandsState } from './types'

/**
 * Опасные команды продажи (оплатить, пробить чек, отменить, «чек уже пробит»), исход которых
 * клиент ещё ждёт. Срок — `session`: F5 и обрыв сети не теряют `operationId`
 * и не разблокируют кнопку «Оплатить», пока чтение продажи не покажет, что
 * случилось. Это не копия продажи: только ЧТО мы отправили.
 */
const initialState: SaleCommandsState = { pending: {} }

interface CommandRef extends SeatRef {
  operationId: string
}

function setPhase(state: SaleCommandsState, ref: CommandRef, phase: SaleCommandPhase): void {
  const entry = state.pending[seatKey(ref)]
  if (entry !== undefined && entry.operationId === ref.operationId) entry.phase = phase
}

const saleCommandsSlice = createSlice({
  name: 'aqsi/saleCommands',
  initialState,
  reducers: {
    saleCommandStarted(state, action: PayloadAction<SaleCommand>) {
      state.pending[seatKey(action.payload)] = action.payload
    },
    /** Началась попытка номер `attempt`: обмен с сервером идёт, пауза кончилась. */
    saleCommandAttempt(state, action: PayloadAction<CommandRef & { attempt: number; at: string }>) {
      const entry = state.pending[seatKey(action.payload)]
      if (entry === undefined || entry.operationId !== action.payload.operationId) return
      entry.attempt = action.payload.attempt
      entry.retryAt = null
      entry.activityAt = action.payload.at
    },
    /** Попытка не удалась по неизвестному исходу: тот же запрос уйдёт ещё раз в `retryAt`. Ответ службы (пусть и отказ) обновляет `activityAt`. */
    saleCommandWaiting(state, action: PayloadAction<CommandRef & { failure: CartSendFailure; retryAt: string; answeredAt: string | null }>) {
      const entry = state.pending[seatKey(action.payload)]
      if (entry === undefined || entry.operationId !== action.payload.operationId) return
      entry.lastFailure = action.payload.failure
      entry.retryAt = action.payload.retryAt
      if (action.payload.answeredAt !== null) entry.activityAt = action.payload.answeredAt
    },
    saleCommandUnknown(state, action: PayloadAction<CommandRef>) {
      setPhase(state, action.payload, 'unknown')
    },
    /** Окно отправки прошло, а продажа так и не узнала команду: можно отдать новую (новый ключ). */
    saleCommandNotArrived(state, action: PayloadAction<CommandRef>) {
      setPhase(state, action.payload, 'not-arrived')
    },
    /** Итог известен: ответ пришёл, служба отказала до эффекта или чтение продажи показало операцию. */
    saleCommandSettled(state, action: PayloadAction<CommandRef>) {
      const key = seatKey(action.payload)
      if (state.pending[key]?.operationId === action.payload.operationId) delete state.pending[key]
    },
    saleCommandsRestored(state, action: PayloadAction<SaleCommandsState>) {
      state.pending = action.payload.pending
    },
  },
  extraReducers: (builder) => {
    builder.addCase(userScopeCleared, () => initialState)
    builder.addCase(resourceScopeCleared, (state, action) => dropResource(state.pending, action.payload.resourceId))
  },
})

export const {
  saleCommandStarted,
  saleCommandAttempt,
  saleCommandWaiting,
  saleCommandUnknown,
  saleCommandNotArrived,
  saleCommandSettled,
  saleCommandsRestored,
} = saleCommandsSlice.actions
export const saleCommandsReducer = saleCommandsSlice.reducer

export function saleCommandOf(state: SaleCommandsState, ref: SeatRef): SaleCommand | undefined {
  return state.pending[seatKey(ref)]
}

/** Команда ещё может изменить деньги или чек: новую опасную команду на этом месте не отдаём. */
export function blocksSaleCommands(command: SaleCommand | undefined): boolean {
  return command !== undefined && command.phase !== 'not-arrived'
}

const KINDS: readonly SaleCommandKind[] = ['pay', 'fiscalize', 'cancel', 'outside']
const PHASES: readonly SaleCommandPhase[] = ['sending', 'unknown', 'not-arrived']
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Проверка сохранённого (D-072). Команда, бывшая «в полёте» на момент F5,
 * читается как `unknown`: ответ потерян вместе со страницей, исход узнаётся
 * чтением продажи, а не повтором.
 */
export function decodeSaleCommands(raw: unknown): SaleCommandsState | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined
  const pending = (raw as { pending?: unknown }).pending
  if (typeof pending !== 'object' || pending === null) return undefined
  const result: SaleCommandsState = { pending: {} }
  for (const value of Object.values(pending as Record<string, unknown>).slice(0, 50)) {
    if (typeof value !== 'object' || value === null) continue
    const entry = value as Record<string, unknown>
    if (
      typeof entry.resourceId !== 'string' ||
      !UUID.test(entry.resourceId) ||
      typeof entry.registerKey !== 'string' ||
      !UUID.test(entry.registerKey) ||
      typeof entry.deviceId !== 'number' ||
      !Number.isSafeInteger(entry.deviceId) ||
      entry.deviceId <= 0 ||
      typeof entry.saleId !== 'string' ||
      !UUID.test(entry.saleId) ||
      typeof entry.operationId !== 'string' ||
      !UUID.test(entry.operationId) ||
      !KINDS.includes(entry.kind as SaleCommandKind) ||
      typeof entry.saleVersion !== 'number' ||
      !Number.isSafeInteger(entry.saleVersion) ||
      typeof entry.startedAt !== 'string' ||
      !PHASES.includes(entry.phase as SaleCommandPhase)
    ) {
      continue
    }
    const command: SaleCommand = {
      resourceId: entry.resourceId,
      registerKey: entry.registerKey,
      deviceId: entry.deviceId,
      kind: entry.kind as SaleCommandKind,
      saleId: entry.saleId,
      saleVersion: entry.saleVersion,
      operationId: entry.operationId,
      startedAt: entry.startedAt,
      phase: entry.phase === 'sending' ? 'unknown' : (entry.phase as SaleCommandPhase),
      // Повторы после F5 не продолжаются: исход читается продажей. Срок «не дошла» отсчитывается от записанного начала.
      attempt: 0,
      retryAt: null,
      activityAt: entry.startedAt,
      lastFailure: null,
    }
    result.pending[seatKey(command)] = command
  }
  return result
}
